'use client';

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const MAX_VISIBLE_TAGS = 2;

export interface ProductTileTagOverflowRowProps {
    productType?: string;
    extraLead?: ReactNode | null;
    highlight?: string;
    eatingStyles: string[];
    className?: string;
}

type TagEntry = { key: string; label: string; el: ReactNode };

/**
 * Single row of PIM / eating tags; shows up to 3 badges, then a +N for the rest.
 * `extraLead` is always rendered and does not count toward the max.
 */
export function ProductTileTagOverflowRow({
    productType,
    extraLead = null,
    highlight,
    eatingStyles,
    className,
}: ProductTileTagOverflowRowProps) {
    const { t } = useTranslation('product');

    const tags: TagEntry[] = [];
    if (highlight) {
        tags.push({
            key: `h-${highlight}`,
            label: highlight,
            el: (
                <Badge
                    hierarchy="primary"
                    schema={productType === 'event' ? 'danger' : 'warning'}
                    className="max-w-full whitespace-normal overflow-visible border-transparent !text-white"
                    title={highlight}>
                    {highlight}
                </Badge>
            ),
        });
    }
    for (const e of eatingStyles) {
        tags.push({
            key: `e-${e}`,
            label: e,
            el: (
                <Badge
                    hierarchy="primary"
                    schema="positive"
                    className="max-w-full whitespace-normal overflow-visible"
                    title={e}>
                    {e}
                </Badge>
            ),
        });
    }

    if (!extraLead && tags.length === 0) {
        return null;
    }

    const visible = tags.slice(0, MAX_VISIBLE_TAGS);
    const rest = tags.length - visible.length;
    const overflowLabel =
        rest > 0
            ? t('tagsOverflowAria', {
                  count: rest,
                  names: tags
                      .slice(MAX_VISIBLE_TAGS)
                      .map((e) => e.label)
                      .join(', '),
              })
            : undefined;

    return (
        <ul
            aria-label={t('tagsListAria')}
            className={cn('m-0 flex w-full min-w-0 max-w-full list-none flex-wrap items-center gap-1 p-0', className)}>
            {extraLead && <li className="inline-flex min-w-0 max-w-full">{extraLead}</li>}
            {visible.map((item) => (
                <li key={item.key} className="inline-flex min-w-0 max-w-full">
                    {item.el}
                </li>
            ))}
            {rest > 0 && (
                <li
                    className="shrink-0 px-0.5 text-xs font-semibold tracking-wider text-[var(--neutral-10)] uppercase"
                    title={overflowLabel}>
                    <span aria-hidden="true">+{rest}</span>
                    <span className="sr-only">{overflowLabel}</span>
                </li>
            )}
        </ul>
    );
}
